import { useFormikContext } from 'formik'
import { useEffect } from 'react'
import { useInView } from 'react-intersection-observer'

import type { ReactElement, ChangeEvent } from 'react'
import type { NavGraphFormValues } from '~/types/navigation-graph'
import type { IPageContainer } from '~/types/page-container'

import SearchInput from '~/components/Inputs/Basic/SearchInput'
import TextInput from '~/components/Inputs/Basic/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalHeader } from '~/components/Modal/Layout'
import MwMLoader from '~/components/MwMLoader'
import PageContainerCard from '~/components/PageContainerCard'
import { DEFAULT_SEARCH, useTablePagination } from '~/hooks/useTablePagination'
import { usePageContainersListInfiniteQuery } from '~/queries/page-containers'
import { debounce } from '~/utils/function'

import './styles.scss'

interface Props {
  capabilities?: string
  isOpen: boolean
  onCloseModal: () => void
  onPageContainerCardClick: (pageContainer: IPageContainer) => void
}

const PageContainersSelectionModal = ({
  capabilities,
  isOpen,
  onCloseModal,
  onPageContainerCardClick,
}: Props): ReactElement => {
  /** Local state */

  const { inView, ref } = useInView()

  const { values } = useFormikContext<NavGraphFormValues>()

  const { filters, page, search, setFilters, setPage, setSearch } = useTablePagination('infinite page containers')

  /** Queries & Mutations */

  const {
    data: pageContainers,
    fetchNextPage,
    isFetchingNextPage,
    status,
  } = usePageContainersListInfiniteQuery(isOpen, {
    ['order-by']: 'created_at DESC',
    page,
    status: 'compiled',
    ...search,
    ...filters,
  })

  /** Effects */

  useEffect(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      'app-key': values.appKey || '',
      capabilities: capabilities?.toLocaleLowerCase() || [],
      'device-type': values.deviceType || '',
    }))
    setPage(0)
  }, [capabilities, setFilters, setPage, values])

  useEffect(() => {
    if (inView && !isFetchingNextPage) {
      fetchNextPage()
      setPage(page + 1)
    }
  }, [fetchNextPage, inView, isFetchingNextPage, page, setPage])

  /** Handlers */

  const handleModalClose = () => {
    setPage(0)
    onCloseModal()
  }

  const hasNextPage = (): boolean => {
    if (pageContainers) {
      return pageContainers.pages[0].total / (pageContainers?.pageParams.length * 10) > 1
    }

    return false
  }

  const handleSearchChange = debounce((event: ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value
    setSearch(text ? { ['title']: text } : DEFAULT_SEARCH)
    setPage(0)
  }, 500)

  const handleTextFilterChange = debounce((event: ChangeEvent<HTMLInputElement>, filterKey: string) => {
    const text = event.target.value
    setFilters((prevFilters) => ({ ...prevFilters, [filterKey]: text }))
    setPage(0)
  }, 500)

  return (
    <Modal className="page-containers-modal" isOpen={isOpen} onCloseModal={handleModalClose}>
      <ModalHeader>Choose a page container</ModalHeader>
      <ModalBody className="page-containers-modal__body">
        <div className="filters-container">
          <SearchInput autoFocus className="filters-container__input" onChange={handleSearchChange} />
          <TextInput
            defaultValue={capabilities?.toLocaleLowerCase()}
            label="Capabilities"
            onChange={(evt) => handleTextFilterChange(evt, 'capabilities')}
            wrapperClassName="filters-container__input"
          />
          <TextInput
            label="Navigation targets"
            onChange={(evt) => handleTextFilterChange(evt, 'navigation-target-outputs')}
            wrapperClassName="filters-container__input"
          />
        </div>
        <div className="cards-container">
          {status === 'error' ? (
            <span>An error has occured</span>
          ) : (
            pageContainers?.pages.map(({ data }) =>
              data ? (
                data.map((pageContainer, idx) => (
                  <PageContainerCard
                    className="cards-container__item"
                    key={`${pageContainer.uuid}-${idx}`}
                    onClick={() => onPageContainerCardClick(pageContainer)}
                    pageContainer={pageContainer}
                    showSubInfos
                  />
                ))
              ) : (
                <div key="no-data">No page containers found</div>
              )
            )
          )}
        </div>
        {hasNextPage() && status !== 'error' && (
          <div ref={ref}>{isFetchingNextPage && <MwMLoader category="item" />}</div>
        )}
      </ModalBody>
    </Modal>
  )
}

export default PageContainersSelectionModal
